import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GitMerge, FileSpreadsheet, ArrowRight, CheckCircle } from "lucide-react";

export default function MultiFileMergePage() {
  const [uploads, setUploads] = useState([]);
  const [form, setForm] = useState({
    left_upload_id: "", right_upload_id: "",
    left_key_column: "", right_key_column: "",
    join_type: "left", result_name: "merged_result", right_prefix: "r_",
  });
  const [leftCols, setLeftCols] = useState([]);
  const [rightCols, setRightCols] = useState([]);
  const [merging, setMerging] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => { client.get("/files/").then(r => setUploads(r.data)); }, []);
  useEffect(() => { if (form.left_upload_id) { const u = uploads.find(u => u._id === form.left_upload_id); setLeftCols(u?.columns?.map(c => c.name) || []); } }, [form.left_upload_id, uploads]);
  useEffect(() => { if (form.right_upload_id) { const u = uploads.find(u => u._id === form.right_upload_id); setRightCols(u?.columns?.map(c => c.name) || []); } }, [form.right_upload_id, uploads]);

  const doMerge = async () => {
    if (!form.left_upload_id || !form.right_upload_id || !form.left_key_column || !form.right_key_column) {
      setError("Please fill in all required fields"); return;
    }
    setMerging(true); setError(""); setResult(null);
    try {
      const res = await client.post("/merge/", form);
      setResult(res.data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setMerging(false); }
  };

  const JOIN_TYPES = [
    { value: "left", label: "Left Join — all rows from left file, matching from right" },
    { value: "inner", label: "Inner Join — only rows with matching keys in both files" },
    { value: "outer", label: "Full Outer Join — all rows from both files" },
  ];

  return (
    <div className="p-6 max-w-3xl mx-auto fade-in">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1"><GitMerge size={18} className="text-amber-600" /><h1 className="text-2xl font-bold text-gray-900">Multi-File Merge</h1></div>
        <p className="text-gray-500 text-sm">Join two uploaded files on a key column — like Excel VLOOKUP but for entire tables</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-5">
        {/* File selectors */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-gray-500 font-semibold">Primary File (left) *</Label>
            <Select value={form.left_upload_id} onValueChange={v => set("left_upload_id", v)}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Select file..." /></SelectTrigger>
              <SelectContent>{uploads.map(u => <SelectItem key={u._id} value={u._id}>{u.filename}</SelectItem>)}</SelectContent>
            </Select>
            {leftCols.length > 0 && (
              <div className="mt-2">
                <Label className="text-xs text-gray-500">Join Key Column *</Label>
                <Select value={form.left_key_column} onValueChange={v => set("left_key_column", v)}>
                  <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Select key..." /></SelectTrigger>
                  <SelectContent>{leftCols.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs text-gray-500 font-semibold">Lookup File (right) *</Label>
            <Select value={form.right_upload_id} onValueChange={v => set("right_upload_id", v)}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Select file..." /></SelectTrigger>
              <SelectContent>{uploads.filter(u => u._id !== form.left_upload_id).map(u => <SelectItem key={u._id} value={u._id}>{u.filename}</SelectItem>)}</SelectContent>
            </Select>
            {rightCols.length > 0 && (
              <div className="mt-2">
                <Label className="text-xs text-gray-500">Join Key Column *</Label>
                <Select value={form.right_key_column} onValueChange={v => set("right_key_column", v)}>
                  <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Select key..." /></SelectTrigger>
                  <SelectContent>{rightCols.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        {/* Join type */}
        <div>
          <Label className="text-xs text-gray-500">Join Type</Label>
          <Select value={form.join_type} onValueChange={v => set("join_type", v)}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>{JOIN_TYPES.map(jt => <SelectItem key={jt.value} value={jt.value}>{jt.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {/* Options */}
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs text-gray-500">Result File Name</Label><Input className="mt-1" value={form.result_name} onChange={e => set("result_name", e.target.value)} /></div>
          <div><Label className="text-xs text-gray-500">Right-Side Column Prefix</Label><Input className="mt-1 font-mono" value={form.right_prefix} onChange={e => set("right_prefix", e.target.value)} placeholder="r_" /></div>
        </div>

        {error && <div className="p-2 bg-red-50 rounded text-red-700 text-sm">{error}</div>}

        <Button className="w-full bg-[#006080] hover:bg-[#004d66] text-white" onClick={doMerge} disabled={merging}>
          <GitMerge size={15} className="mr-2" />{merging ? "Merging..." : "Merge Files"}
        </Button>
      </div>

      {result && (
        <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2"><CheckCircle size={18} className="text-green-600" /><p className="font-semibold text-green-700">Merge complete!</p></div>
          <div className="flex gap-4 text-sm text-green-600 mb-3">
            <span><strong>{result.row_count.toLocaleString()}</strong> rows in result</span>
            <span><strong>{result.columns?.length}</strong> columns</span>
            <span className="capitalize">{result.join_type} join</span>
          </div>
          <Button size="sm" className="bg-[#006080] text-white" onClick={() => navigate(`/staging/${result.upload_id}`)}>
            View Merged File <ArrowRight size={13} className="ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}
